import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import { H3 } from '../Heading';
import { IconCancel, IconChevronLeft } from '../Icon';
import { LinkButton, LinkButtonContent } from '../LinkButton';
import { Portal, PortalNamespace } from '../Portal';
import {
  animationDuration,
  Body,
  Close,
  CloseBtn,
  Footer,
  Header,
  Heading,
  PanelWrapper,
  UiBlock,
  Wrapper,
} from './wrapper';

export { Body as ShallowPanelBody };
export { Footer as ShallowPanelFooter };

export interface IPanelPortalNavBack {
  label: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
}

interface IPanelPortal {
  id?: string | null;
  title: string | null;
  onClose?: () => void;
  children?: React.ReactNode;
  onNavBack?: IPanelPortalNavBack;
  wide?: boolean;
  leftAdjust?: boolean;
  disableAnimation?: boolean;
}

const ShallowPanel = ({
  id,
  title,
  onClose,
  children,
  onNavBack,
  wide,
  leftAdjust,
  disableAnimation,
}: IPanelPortal) => {
  const { t } = useTranslation('common');
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [animate, setAnimate] = useState(true);

  const onCloseAnimate = useCallback(() => {
    if (onClose) {
      setAnimate(false);
      setTimeout(() => onClose(), disableAnimation ? 0 : animationDuration);
    }
  }, [onClose, disableAnimation]);

  // Close on escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseAnimate();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseAnimate]);

  return (
    <Portal id={id || PortalNamespace.DefaultPortal}>
      <CSSTransition
        nodeRef={nodeRef}
        in={animate}
        appear={!disableAnimation}
        timeout={disableAnimation ? 0 : animationDuration}
        classNames={disableAnimation ? '' : 'panel'}
      >
        <Wrapper ref={nodeRef}>
          <UiBlock
            title={t('closePanel', { ns: 'common' }) || ''}
            onClick={onCloseAnimate}
            $hideOnLargeScreen={leftAdjust}
          />
          <PanelWrapper $wide={wide} $leftAdjust={leftAdjust}>
            <Header>
              <Heading>
                {onNavBack ? (
                  <LinkButton
                    onClick={onNavBack.onClick}
                    $disabled={onNavBack.disabled}
                    title={onNavBack.title || onNavBack.label}
                    $hasContent
                  >
                    <LinkButtonContent>
                      <IconChevronLeft />
                    </LinkButtonContent>
                    <LinkButtonContent>{onNavBack.label}</LinkButtonContent>
                  </LinkButton>
                ) : (
                  <H3 as="h2" $margin="none">
                    {title}
                  </H3>
                )}
              </Heading>
              <Close>
                <CloseBtn
                  title={t('closePanel', { ns: 'common' }) || ''}
                  aria-label={t('closePanel', { ns: 'common' }) || undefined}
                  onClick={onCloseAnimate}
                >
                  <IconCancel />
                </CloseBtn>
              </Close>
            </Header>
            {children}
          </PanelWrapper>
        </Wrapper>
      </CSSTransition>
    </Portal>
  );
};

export default ShallowPanel;
